// src/services/officialRentData.ts
/**
 * Service for fetching official rent data from HUD SAFMR
 * (Small Area Fair Market Rents)
 *
 * Data source: https://www.huduser.gov/portal/datasets/fmr/smallarea/index.html
 *
 * This service:
 * 1. Fetches official rent averages by ZIP code from Firestore
 * 2. Provides national averages as fallback
 * 3. Validates user submissions against official data (outlier detection)
 */

import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { BillCategory } from '../widgets/widgets/WhatAreYouPaying';

/**
 * Official rent data structure stored in Firestore
 * Collection: officialRentData/{zipCode}
 */
export interface OfficialRentData {
	zipCode: string;
	rent0br: number; // Studio/efficiency
	rent1br: number; // 1 bedroom
	rent2br: number; // 2 bedroom (most common reference)
	rent3br: number; // 3 bedroom
	rent4br: number; // 4 bedroom
	source: string; // e.g., "HUD_SAFMR_FY2026"
	updatedAt: Date;
}

/**
 * Category-specific official averages for a ZIP code
 */
export interface OfficialCategoryAverages {
	rent: number;
	utilities: number;
	insurance: number;
	phone: number;
	internet: number;
	other: number;
}

/**
 * Result of fetching official data
 */
export interface OfficialDataResult {
	averages: OfficialCategoryAverages;
	source: 'official' | 'national_fallback';
	zipCode: string;
	dataYear?: string;
}

/**
 * Outlier check result
 */
export interface OutlierCheckResult {
	isOutlier: boolean;
	reason?: string;
	officialAverage: number;
	percentageDiff: number;
}

/**
 * National average fallbacks (used when ZIP-specific data unavailable)
 * Based on HUD FY2024 national medians and industry averages
 */
export const NATIONAL_AVERAGES: OfficialCategoryAverages = {
	rent: 1450, // National median ~$1,400-1,500
	utilities: 150, // Electric + gas + water avg
	insurance: 120, // Renters insurance avg
	phone: 85, // Cell phone plan avg
	internet: 65, // Broadband avg
	other: 100, // Misc recurring bills
};

/**
 * Thresholds for outlier detection
 * Bills outside these ranges are flagged
 */
const OUTLIER_THRESHOLDS = {
	// Bill is flagged if it's more than X times the official average
	upperMultiplier: 3.0, // 300% of average
	// Bill is flagged if it's less than X times the official average
	lowerMultiplier: 0.1, // 10% of average
};

/**
 * Category-specific multipliers for deriving non-rent averages
 * from rent data (since HUD only provides rent data)
 */
const CATEGORY_RENT_RATIOS: Record<BillCategory, number> = {
	rent: 1.0, // Direct from HUD
	utilities: 0.103, // ~10.3% of rent nationally
	insurance: 0.083, // ~8.3% of rent
	phone: 0.059, // ~5.9% of rent
	internet: 0.045, // ~4.5% of rent
	other: 0.069, // ~6.9% of rent
};

/**
 * Fetch official rent data for a specific ZIP code
 */
export async function getOfficialRentData(
	zipCode: string
): Promise<OfficialRentData | null> {
	try {
		const docRef = doc(collection(db, 'officialRentData'), zipCode);
		const snapshot = await getDoc(docRef);

		if (!snapshot.exists()) {
			return null;
		}

		const data = snapshot.data();
		return {
			zipCode: data.zipCode,
			rent0br: data.rent0br,
			rent1br: data.rent1br,
			rent2br: data.rent2br,
			rent3br: data.rent3br,
			rent4br: data.rent4br,
			source: data.source,
			updatedAt: data.updatedAt?.toDate() || new Date(),
		};
	} catch (error) {
		console.error('Error fetching official rent data:', error);
		return null;
	}
}

/**
 * Get official category averages for a ZIP code
 * Falls back to national averages if ZIP data unavailable
 */
export async function getOfficialAverages(
	zipCode: string
): Promise<OfficialDataResult> {
	const rentData = await getOfficialRentData(zipCode);

	if (rentData) {
		// Use 2BR rent as the base reference (most common comparison)
		const baseRent = rentData.rent2br;

		// Derive other category averages from rent using national ratios
		const averages: OfficialCategoryAverages = {
			rent: baseRent,
			utilities: Math.round(baseRent * CATEGORY_RENT_RATIOS.utilities),
			insurance: Math.round(baseRent * CATEGORY_RENT_RATIOS.insurance),
			phone: Math.round(baseRent * CATEGORY_RENT_RATIOS.phone),
			internet: Math.round(baseRent * CATEGORY_RENT_RATIOS.internet),
			other: Math.round(baseRent * CATEGORY_RENT_RATIOS.other),
		};

		// Extract year from source (e.g., "HUD_SAFMR_FY2026" -> "2026")
		const yearMatch = rentData.source?.match(/\d{4}/);
		const dataYear = yearMatch ? yearMatch[0] : undefined;

		return {
			averages,
			source: 'official',
			zipCode,
			dataYear,
		};
	}

	// Fallback to national averages
	return {
		averages: { ...NATIONAL_AVERAGES },
		source: 'national_fallback',
		zipCode,
	};
}

/**
 * Check if a bill amount is an outlier compared to official data
 * Used to flag potentially erroneous user submissions
 */
export async function checkForOutlier(
	zipCode: string,
	category: BillCategory,
	amount: number
): Promise<OutlierCheckResult> {
	const { averages } = await getOfficialAverages(zipCode);
	const officialAverage = averages[category];

	const percentageDiff = ((amount - officialAverage) / officialAverage) * 100;

	// Check upper bound (e.g., $10,000 rent when average is $1,500)
	if (amount > officialAverage * OUTLIER_THRESHOLDS.upperMultiplier) {
		return {
			isOutlier: true,
			reason: `Amount is ${Math.round(percentageDiff)}% above the area average`,
			officialAverage,
			percentageDiff,
		};
	}

	// Check lower bound (e.g., $50 rent when average is $1,500)
	if (amount < officialAverage * OUTLIER_THRESHOLDS.lowerMultiplier) {
		return {
			isOutlier: true,
			reason: `Amount is ${Math.round(Math.abs(percentageDiff))}% below the area average`,
			officialAverage,
			percentageDiff,
		};
	}

	return {
		isOutlier: false,
		officialAverage,
		percentageDiff,
	};
}

/**
 * Get comparison data combining official and community averages
 */
export interface ComparisonData {
	officialAvg: number;
	communityAvg: number | null;
	communityCount: number;
	source: 'official' | 'national_fallback';
	dataYear?: string;
}

export async function getComparisonData(
	zipCode: string,
	category: BillCategory,
	communityStats?: { avg: number; count: number } | null
): Promise<ComparisonData> {
	const { averages, source, dataYear } = await getOfficialAverages(zipCode);

	return {
		officialAvg: averages[category],
		communityAvg: communityStats?.avg ?? null,
		communityCount: communityStats?.count ?? 0,
		source,
		dataYear,
	};
}

/**
 * Get rent estimate for different bedroom counts
 * Useful for more detailed rent comparisons
 */
export async function getRentByBedrooms(
	zipCode: string
): Promise<Record<string, number> | null> {
	const rentData = await getOfficialRentData(zipCode);

	if (!rentData) {
		return null;
	}

	return {
		studio: rentData.rent0br,
		'1br': rentData.rent1br,
		'2br': rentData.rent2br,
		'3br': rentData.rent3br,
		'4br': rentData.rent4br,
	};
}
